/**
 * Диагностика аппаратных возможностей камеры
 */
import { useState, useRef, useEffect } from 'react'

interface DiagnosticsResult {
  devices: MediaDeviceInfo[]
  currentDevice: string
  trackCapabilities: any
  trackSettings: any
  supportedConstraints: MediaTrackSupportedConstraints | null
  imageCaptureSupported: boolean
  photoCapabilities: any
  photoSettings: any
  errors: string[]
}

export function CameraDiagnostics() {
  const [result, setResult] = useState<DiagnosticsResult | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const runDiagnostics = async () => {
    setIsRunning(true)
    const errors: string[] = []
    const diagnostics: Partial<DiagnosticsResult> = {
      errors: [],
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      diagnostics.devices = devices.filter(d => d.kind === 'videoinput')

      diagnostics.supportedConstraints = navigator.mediaDevices.getSupportedConstraints()

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      })
      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }

      const videoTrack = stream.getVideoTracks()[0]
      diagnostics.currentDevice = videoTrack.label
      diagnostics.trackCapabilities = videoTrack.getCapabilities()
      diagnostics.trackSettings = videoTrack.getSettings()

      diagnostics.imageCaptureSupported = 'ImageCapture' in window

      if (diagnostics.imageCaptureSupported) {
        try {
          // @ts-ignore
          const imageCapture = new ImageCapture(videoTrack)
          // @ts-ignore
          diagnostics.photoCapabilities = await imageCapture.getPhotoCapabilities()
          // @ts-ignore
          diagnostics.photoSettings = await imageCapture.getPhotoSettings()
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err)
          errors.push(`ImageCapture error: ${errMsg}`)
        }
      }

      const caps = diagnostics.trackCapabilities || {}
      
      if ('brightness' in caps) {
        try {
          await videoTrack.applyConstraints({ 
            // @ts-ignore
            brightness: caps.brightness?.min || 0 
          })
        } catch (err) {
          errors.push(`brightness apply failed: ${err}`)
        }
      }

      if ('exposureCompensation' in caps) {
        try {
          await videoTrack.applyConstraints({ 
            // @ts-ignore
            exposureCompensation: caps.exposureCompensation?.min || -2
          })
        } catch (err) {
          errors.push(`exposureCompensation apply failed: ${err}`)
        }
      }

      diagnostics.errors = errors

    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err)
      errors.push(`General error: ${errMsg}`)
      diagnostics.errors = errors
    }

    setResult(diagnostics as DiagnosticsResult)
    setIsRunning(false)
  }

  const stopDiagnostics = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setResult(null)
  }

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  const copyResults = () => {
    if (result) {
      navigator.clipboard.writeText(JSON.stringify(result, null, 2))
      alert('Результаты скопированы!')
    }
  }

  return (
    <div className="space-y-6">
      {/* Actions - OneUI buttons */}
      <div className="flex gap-4">
        {!result ? (
          <button 
            onClick={runDiagnostics} 
            disabled={isRunning}
            className="btn btn-lg btn-primary flex-1"
          >
            {isRunning ? (
              <>
                <div className="loading-spinner w-5 h-5 border-2" />
                <span>Проверка...</span>
              </>
            ) : (
              <>
                <span>▶️</span>
                <span>Запустить диагностику</span>
              </>
            )}
          </button>
        ) : (
          <>
            <button 
              onClick={stopDiagnostics} 
              className="btn btn-lg btn-danger flex-1"
            >
              <span>⏹️</span>
              <span>Остановить</span>
            </button>
            <button 
              onClick={copyResults} 
              className="btn btn-lg btn-secondary flex-1"
            >
              <span>📋</span>
              <span>Копировать</span>
            </button>
          </>
        )}
      </div>

      {/* Video Preview - OneUI focus block */}
      {result && (
        <div className="focus-block p-0 overflow-hidden">
          <div className="aspect-video bg-black">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
              autoPlay
            />
          </div>
        </div>
      )}

      {/* Results - OneUI cards */}
      {result && (
        <div className="space-y-5">
          {/* Current Device */}
          <div className="focus-block">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xl">📷</span>
              <span className="text-[15px] font-semibold text-white">Текущая камера</span>
            </div>
            <p className="text-[15px] text-[#8e8e93]">{result.currentDevice || 'Неизвестно'}</p>
          </div>

          {/* ImageCapture API */}
          <div className="focus-block">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xl">🖼️</span>
              <span className="text-[15px] font-semibold text-white">ImageCapture API</span>
            </div>
            <span className={`badge ${result.imageCaptureSupported ? 'badge-success' : 'badge-error'}`}>
              {result.imageCaptureSupported ? '✅ Поддерживается' : '❌ Не поддерживается'}
            </span>
          </div>

          {/* Capabilities */}
          {result.trackCapabilities && (
            <div className="focus-block">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xl">⚙️</span>
                <span className="text-[15px] font-semibold text-white">Hardware Capabilities</span>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(result.trackCapabilities).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-start gap-3 text-[12px]">
                    <span className="text-[#636366] font-mono">{key}:</span>
                    <span className="text-[#8e8e93] text-right break-all">
                      {typeof value === 'object' 
                        ? JSON.stringify(value) 
                        : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Errors */}
          {result.errors && result.errors.length > 0 && (
            <div className="focus-block bg-[#ff453a]/10 border border-[#ff453a]/30">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xl">⚠️</span>
                <span className="text-[15px] font-semibold text-[#ff453a]">Ошибки</span>
              </div>
              <ul className="space-y-2">
                {result.errors.map((err, i) => (
                  <li key={i} className="text-[13px] text-[#ff453a]/90">{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Conclusion */}
          <div className={`focus-block ${
            result.trackCapabilities && 
              ('brightness' in result.trackCapabilities || 
               'exposureCompensation' in result.trackCapabilities)
              ? 'bg-[#30d158]/10 border border-[#30d158]/30'
              : 'bg-[#ff9f0a]/10 border border-[#ff9f0a]/30'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xl">📝</span>
              <span className="text-[15px] font-semibold text-white">Заключение</span>
            </div>
            {result.trackCapabilities && 
             ('brightness' in result.trackCapabilities || 
              'exposureCompensation' in result.trackCapabilities) ? (
              <p className="text-[14px] text-[#30d158]">
                ✅ Камера поддерживает аппаратное управление! 
                Можно настроить brightness/exposure через hardware constraints.
              </p>
            ) : (
              <div className="text-[14px] text-[#ff9f0a] space-y-3">
                <p>
                  ❌ Фронтальная камера НЕ поддерживает аппаратное управление.
                </p>
                <div>
                  <p className="font-semibold mb-2">Варианты решения:</p>
                  <ul className="list-disc list-inside space-y-1 text-[13px]">
                    <li>Использовать CSS фильтры (текущий подход)</li>
                    <li>Попросить снизить яркость телефона</li>
                    <li>Использовать заднюю камеру</li>
                    <li>Физический фильтр на камеру</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}



